import { api } from "_shared/_stores/api";
import { logSchemaFailure } from "_shared/_utilities/log-schema-failure";
import EntityMember from "_shared/entity-members/entity-member";
import ProposalSchema, { ProposalJSON } from "work/proposals/_schemas/proposal-schema";
import { WorkTeamSide } from "work/work-agreements/teams/leader-handover/team-leader-handover";

type ProposalHandoverArgs = { proposalId: string };

/**
 * Changing who leads a team while a proposal is negotiated (#5836). Every command answers with the
 * proposal: it carries the pending handover while it waits, and the new leader once it applies.
 */
export const proposalLeaderHandoverApi = api.injectEndpoints({
  endpoints: (build) => ({
    requestProposalLeaderHandover: build.mutation<
      ProposalJSON,
      ProposalHandoverArgs & {
        side: WorkTeamSide;
        successor: EntityMember;
        outgoingLeaderStays: boolean;
        grantSigningAuthority: boolean;
      }
    >({
      query: ({ proposalId, side, successor, outgoingLeaderStays, grantSigningAuthority }) => ({
        url: `work/proposals/${proposalId}/leader-handover`,
        method: "POST",
        body: {
          side,
          successor: { userId: successor.userId.value, entityId: successor.legalEntityId.value },
          outgoingLeaderStays,
          grantSigningAuthority,
        },
      }),
      rawResponseSchema: ProposalSchema.ProposalJSONZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: (_result, _error, args) => [{ type: "Proposal", id: args.proposalId }],
    }),
    acceptProposalLeaderHandover: build.mutation<ProposalJSON, ProposalHandoverArgs>({
      query: ({ proposalId }) => ({ url: `work/proposals/${proposalId}/leader-handover/accept`, method: "POST", body: {} }),
      rawResponseSchema: ProposalSchema.ProposalJSONZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: (_result, _error, args) => [{ type: "Proposal", id: args.proposalId }],
    }),
    approveProposalLeaderHandover: build.mutation<
      ProposalJSON,
      ProposalHandoverArgs & { outgoingLeaderStays?: boolean; grantSigningAuthority?: boolean }
    >({
      query: ({ proposalId, outgoingLeaderStays, grantSigningAuthority }) => ({
        url: `work/proposals/${proposalId}/leader-handover/approve`,
        method: "POST",
        body: { outgoingLeaderStays, grantSigningAuthority },
      }),
      rawResponseSchema: ProposalSchema.ProposalJSONZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: (_result, _error, args) => [{ type: "Proposal", id: args.proposalId }],
    }),
    declineProposalLeaderHandover: build.mutation<ProposalJSON, ProposalHandoverArgs>({
      query: ({ proposalId }) => ({ url: `work/proposals/${proposalId}/leader-handover/decline`, method: "POST", body: {} }),
      rawResponseSchema: ProposalSchema.ProposalJSONZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: (_result, _error, args) => [{ type: "Proposal", id: args.proposalId }],
    }),
    // A client leader can have two offers waiting -- their own team's and a new vendor leader -- so
    // the team says which one to take back.
    withdrawProposalLeaderHandover: build.mutation<ProposalJSON, ProposalHandoverArgs & { side: WorkTeamSide }>({
      query: ({ proposalId, side }) => ({
        url: `work/proposals/${proposalId}/leader-handover`,
        method: "DELETE",
        params: { side },
      }),
      rawResponseSchema: ProposalSchema.ProposalJSONZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: (_result, _error, args) => [{ type: "Proposal", id: args.proposalId }],
    }),
  }),
});

export const {
  useRequestProposalLeaderHandoverMutation,
  useAcceptProposalLeaderHandoverMutation,
  useApproveProposalLeaderHandoverMutation,
  useDeclineProposalLeaderHandoverMutation,
  useWithdrawProposalLeaderHandoverMutation,
} = proposalLeaderHandoverApi;
